import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { useAuthProvider } from "../../auth/hooks/auth-provider";
import { UserRole } from "../../models/UserRole";
import {
  useInternalUserProfile,
  useListInternalUsers,
  useUpdateInternalUserProfile,
} from "../../hooks/useInternalUsers";
import { zoullooColors } from "../../theme";

/**
 * Profile page for internal users (CSM, TAM, Admin).
 *
 * - Any internal user can edit their OWN profile (PhoneNumber, JobTitle, 4 scheduling URLs).
 * - Admin can additionally pick another internal user from a dropdown to edit theirs.
 *
 * Backend permissions are double-checked in InternalUsersController (self or admin).
 */
const InternalUserProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthProvider();

  const isAdmin = user.roles?.includes(UserRole.Admin) ?? false;

  // Selected user being edited. Defaults to the current user.
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    typeof user.id === "number" ? user.id : null
  );

  // List of internal users — only fetched for admins (used by the picker)
  const { data: internalUsers = [] } = useListInternalUsers(isAdmin);

  // Profile of the selected user
  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
  } = useInternalUserProfile(selectedUserId);

  const updateMutation = useUpdateInternalUserProfile();

  // Form state — synced from profile when it changes
  const [phoneNumber, setPhoneNumber] = useState("");
  const [schedulingUrlClientFr, setSchedulingUrlClientFr] = useState("");
  const [schedulingUrlClientEn, setSchedulingUrlClientEn] = useState("");
  const [schedulingUrlTalentFr, setSchedulingUrlTalentFr] = useState("");
  const [schedulingUrlTalentEn, setSchedulingUrlTalentEn] = useState("");

  // The scheduling URLs are only relevant for CSM (who books meetings with clients/talents)
  // and for Admin (who manages CSM profiles). TAM users don't need them.
  // Backend returns roles in lowercase already (matches UserRole enum values),
  // but normalize defensively in case the JWT or seed data uses a different case.
  const profileHasSchedulingUrls = !!profile?.roles.some((r) => {
    const normalized = r.toLowerCase();
    return normalized === UserRole.CSM || normalized === UserRole.Admin;
  });

  // Snackbar
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error">("success");

  // Hydrate the form from the loaded profile
  useEffect(() => {
    if (profile) {
      setPhoneNumber(profile.phoneNumber ?? "");
      setSchedulingUrlClientFr(profile.schedulingUrlClientFr ?? "");
      setSchedulingUrlClientEn(profile.schedulingUrlClientEn ?? "");
      setSchedulingUrlTalentFr(profile.schedulingUrlTalentFr ?? "");
      setSchedulingUrlTalentEn(profile.schedulingUrlTalentEn ?? "");
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) return;

    /**
     * Resolve the value to send for a scheduling URL field:
     * - If the profile owns these URLs (CSM/Admin) → send the trimmed form value.
     * - Otherwise (TAM) → preserve whatever's already in DB so a TAM viewing
     *   the form doesn't accidentally clear another user's data.
     */
    const resolveSchedulingUrl = (
      formValue: string,
      currentDbValue: string | null | undefined
    ): string | null =>
      profileHasSchedulingUrls
        ? formValue.trim() || null
        : currentDbValue ?? null;

    try {
      await updateMutation.mutateAsync({
        userId: selectedUserId,
        data: {
          phoneNumber: phoneNumber.trim() || null,
          // Keep the JobTitle untouched from the frontend (still settable via API or seed).
          // Preserve whatever value is currently in DB by sending the loaded profile's value.
          jobTitle: profile?.jobTitle ?? null,
          schedulingUrlClientFr: resolveSchedulingUrl(schedulingUrlClientFr, profile?.schedulingUrlClientFr),
          schedulingUrlClientEn: resolveSchedulingUrl(schedulingUrlClientEn, profile?.schedulingUrlClientEn),
          schedulingUrlTalentFr: resolveSchedulingUrl(schedulingUrlTalentFr, profile?.schedulingUrlTalentFr),
          schedulingUrlTalentEn: resolveSchedulingUrl(schedulingUrlTalentEn, profile?.schedulingUrlTalentEn),
        },
      });
      setAlertSeverity("success");
      setAlertMessage(t("internalUserProfile.saved", "Profil enregistré"));
      setAlertOpen(true);
    } catch (err) {
      console.error("Error saving internal user profile:", err);
      setAlertSeverity("error");
      setAlertMessage(t("internalUserProfile.saveError", "Erreur lors de l'enregistrement"));
      setAlertOpen(true);
    }
  };

  return (
    <Box px={6} py={4}>
      <Typography variant="h2" mb={4}>
        {isAdmin
          ? t("internalUserProfile.adminTitle", "Profils internes")
          : t("internalUserProfile.title", "Mon profil")}
      </Typography>

      {/* Admin user picker — only visible to admins */}
      {isAdmin && (
        <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            {t("internalUserProfile.pickUser", "Sélectionner un utilisateur à éditer")}
          </Typography>
          <Autocomplete
            options={internalUsers}
            getOptionLabel={(opt) =>
              `${opt.firstName ?? ""} ${opt.lastName ?? ""}`.trim() ||
              opt.email
            }
            value={
              internalUsers.find((u) => u.userId === selectedUserId) ??
              null
            }
            onChange={(_, newValue) => {
              setSelectedUserId(newValue?.userId ?? null);
            }}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            renderOption={(props, option) => (
              <li {...props} key={option.userId}>
                <Box>
                  <Typography variant="body2">
                    {`${option.firstName ?? ""} ${option.lastName ?? ""}`.trim() || option.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email} — {option.roles.join(", ")}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label={t("internalUserProfile.user", "Utilisateur")} />
            )}
          />
        </Paper>
      )}

      <Paper elevation={3} sx={{ p: 4, maxWidth: 800 }}>
        {profileLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : profileError || !profile ? (
          <Alert severity="error">
            {t("internalUserProfile.loadError", "Impossible de charger le profil.")}
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Read-only identity block */}
            <Box mb={3}>
              <Typography variant="h5">
                {`${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim() || profile.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {profile.email}
              </Typography>
              <Stack direction="row" spacing={1} mt={1}>
                {profile.roles.map((role) => (
                  <Chip
                    key={role}
                    label={role.toUpperCase()}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t("internalUserProfile.phoneNumber", "Téléphone")}
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+33 6 12 34 56 78"
                  helperText={t(
                    "internalUserProfile.phoneNumberHelp",
                    "Affiché dans les emails Account Management comme contact dédié."
                  )}
                />
              </Grid>

              {/* URLs de RDV : visibles uniquement pour CSM et Admin (pas TAM). */}
              {profileHasSchedulingUrls && (
                <>
                  <Grid size={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mt: 1 }}>
                      {t(
                        "internalUserProfile.schedulingUrlsTitle",
                        "URLs de prise de rendez-vous"
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        "internalUserProfile.schedulingUrlsHelp",
                        "Affichées dans les emails Account Management. La bonne URL est sélectionnée automatiquement selon le destinataire (Client/Talent) et sa langue."
                      )}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t("internalUserProfile.schedulingUrlClientFr", "URL Client — FR")}
                      value={schedulingUrlClientFr}
                      onChange={(e) => setSchedulingUrlClientFr(e.target.value)}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t("internalUserProfile.schedulingUrlClientEn", "URL Client — EN")}
                      value={schedulingUrlClientEn}
                      onChange={(e) => setSchedulingUrlClientEn(e.target.value)}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t("internalUserProfile.schedulingUrlTalentFr", "URL Talent — FR")}
                      value={schedulingUrlTalentFr}
                      onChange={(e) => setSchedulingUrlTalentFr(e.target.value)}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t("internalUserProfile.schedulingUrlTalentEn", "URL Talent — EN")}
                      value={schedulingUrlTalentEn}
                      onChange={(e) => setSchedulingUrlTalentEn(e.target.value)}
                      placeholder="https://..."
                    />
                  </Grid>
                </>
              )}

              <Grid size={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={updateMutation.isPending}
                  sx={{
                    mt: 2,
                    backgroundColor: zoullooColors.secondary.main,
                    color: zoullooColors.secondary.contrastText,
                    "&:hover": { backgroundColor: zoullooColors.secondary.dark },
                  }}
                >
                  {updateMutation.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t("internalUserProfile.save", "Enregistrer")
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InternalUserProfile;
